/**
 * useLiveAvatarConfig - Derived avatar model/config for live mode
 *
 * Extracts the linked-avatar resolution and shared embedded config so App.tsx
 * only consumes the live avatar data it needs.
 */

import { MutableRefObject, useMemo } from 'react';

import {
  getModelById,
  resolveModelUrlForQuality,
  type ModelConfig,
  type AvatarQuality
} from '../modelRegistry';
import { mergeAvatarRuntimeConfig, resolveAvatarTtsVoiceId } from '../avatarRuntimeConfig';
import type { CamlinAvatarConfig } from '../../sdk/CamlinAvatar';
import { useAvatarRuntimeConfig } from './useAvatarRuntimeConfig';

/**
 * Detect mobile WebKit / Android Chrome at hook init time. Used to force the
 * SD model on phones regardless of the project's `avatarQuality` setting,
 * because iOS Safari WKWebView has a tight WebGL memory budget that the HD
 * model (97 morph targets + 35MB GLB) blows past mid-render — the page is
 * killed by the OS before any error handler can intervene.
 *
 * Static at module load so we don't have to thread it through props or
 * re-derive on every render. SSR-safe: returns `false` when window is
 * undefined.
 */
const IS_MOBILE_USER_AGENT: boolean = (() => {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod|Android|Mobile/i.test(ua)) {
    return true;
  }
  // iPadOS 13+ pretends to be desktop Safari but exposes touch points.
  if ((navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1 && /Macintosh/.test(ua)) {
    return true;
  }
  return false;
})();

/**
 * Live-surface quality resolution (Aug 17 2026 — extracted so the contract is
 * testable and pinned):
 *
 * - Explicit `'hd'` / `'sd'` (from the avatar settings chain via
 *   `linkedAvatarConfig.avatarQuality`) is honoured on EVERY device,
 *   including mobile. An explicit HD save is a deliberate per-workspace act,
 *   so it overrides the mobile SD force (Keith, Aug 17 2026). The OOM risk
 *   that motivated the force (iOS WKWebView killing the page mid-load on the
 *   old 35MB GLBs) is called out in the settings modal help text; the rebuilt
 *   HD models are ~25MB.
 * - Unset / invalid ("Auto") preserves the historical behaviour exactly:
 *   SD on live surfaces (the April 2026 fast-load default for Visual IVR and
 *   the live host) and SD forced on mobile user agents.
 */

/** The quality "Auto" resolves to on desktop live surfaces. */
export const LIVE_AUTO_DEFAULT_QUALITY: AvatarQuality = 'sd';

export function resolveRequestedAvatarQuality(
  requested: unknown,
  isMobile: boolean = IS_MOBILE_USER_AGENT
): AvatarQuality {
  if (requested === 'hd' || requested === 'sd') {
    return requested;
  }
  // Auto, mobile: always SD to protect WKWebView's WebGL memory budget.
  if (isMobile) {
    return 'sd';
  }
  // Auto, desktop: live surfaces default to SD (April 2026 fast-load
  // choice). Kept separate from the mobile force on purpose — if this
  // default ever moves to HD, mobile must stay protected above.
  return LIVE_AUTO_DEFAULT_QUALITY;
}

export interface UseLiveAvatarConfigOptions {
  selectedModel: ModelConfig;
  linkedAvatarModelId: string | null;
  linkedAvatarConfig: Record<string, unknown> | null;
  projectToken?: string | null;
  runtimeUrl?: string | null;
  requestedLiveVoiceId?: string;
  liveModeActive: boolean;
  isEmbeddedBuilderView: boolean;
  cameraZoom: number;
  isMobileCall: boolean;
  isWebrtcCall: boolean;
  isInVoiceCall: boolean;
  liveAvatarModelIdRef: MutableRefObject<string | null>;
  resolveLinkedAvatarModelId: (
    explicitAvatarModelId?: string | null,
    avatarConfig?: Record<string, unknown> | null
  ) => string | null;
}

export interface UseLiveAvatarConfigReturn {
  activeAvatarModel: ModelConfig;
  /**
   * Quality-resolved + mobile-overridden model URL, ready to hand to
   * `<Scene modelUrl={...} modelKey={...}>` or `<CamlinAvatar modelUrl>`.
   *
   * IMPORTANT: This is the value the renderer should consume — NOT
   * `activeAvatarModel.url`, which is always the HD URL regardless of
   * device. Routing the HD URL into a phone-class device's <Canvas> is
   * how iPhone Safari WKWebView ends up OOM-killing the page mid-load.
   * See AvatarSceneShell.tsx and App.tsx for the wiring.
   */
  resolvedModelUrl: string;
  /**
   * Merged appearance overrides for the avatar:
   *   project blueprint settings (skinColor / eyeColor / hairColor / backgroundColor / etc.)
   *   ⊕ model registry's `defaultAppearance` (per-model fallbacks)
   *
   * App.tsx must dispatch this as an `avatar:config:update` event once
   * the avatar reports ready (Avatar.tsx is the listener). The renderer
   * will NOT pick it up from props alone — the appearance pipeline runs
   * imperatively via window event so it can be replayed on model swap
   * without a full React rerender.
   *
   * Returns null when there are no overrides at all (use the GLB's
   * baked-in materials).
   */
  liveAvatarAppearance: Record<string, unknown> | null;
  effectiveCameraZoom: number;
  motionParallaxEnabled: boolean;
  effectiveSkipTeethAnimation: boolean | undefined;
  liveTtsVoiceId?: string;
  sharedLiveAvatarConfig: CamlinAvatarConfig;
  liveAvatarConfig: Record<string, unknown> | null;
}

export function useLiveAvatarConfig({
  selectedModel,
  linkedAvatarModelId,
  linkedAvatarConfig,
  projectToken,
  runtimeUrl,
  requestedLiveVoiceId,
  liveModeActive,
  isEmbeddedBuilderView,
  cameraZoom,
  isMobileCall,
  isWebrtcCall,
  isInVoiceCall,
  liveAvatarModelIdRef,
  resolveLinkedAvatarModelId
}: UseLiveAvatarConfigOptions): UseLiveAvatarConfigReturn {
  const resolvedLinkedAvatarModelId = useMemo(() => (
    resolveLinkedAvatarModelId(linkedAvatarModelId, linkedAvatarConfig)
  ), [linkedAvatarConfig, linkedAvatarModelId, resolveLinkedAvatarModelId]);

  const runtimeLinkedModel = useMemo(() => {
    if (!resolvedLinkedAvatarModelId) {
      return null;
    }

    return getModelById(resolvedLinkedAvatarModelId) ?? null;
  }, [resolvedLinkedAvatarModelId]);

  const activeAvatarModel = runtimeLinkedModel ?? selectedModel;
  liveAvatarModelIdRef.current = activeAvatarModel.id;

  const runtimeProjectToken = useMemo(() => {
    const linkedProjectToken = typeof linkedAvatarConfig?.projectToken === 'string'
      ? linkedAvatarConfig.projectToken.trim()
      : '';
    return linkedProjectToken || projectToken?.trim() || '__platform-default__';
  }, [linkedAvatarConfig, projectToken]);

  const {
    config: savedModelConfig
  } = useAvatarRuntimeConfig({
    enabled: Boolean(activeAvatarModel.id),
    projectToken: runtimeProjectToken,
    modelId: activeAvatarModel.id,
    runtimeUrl
  });

  const effectiveCameraZoom = useMemo(() => {
    if (isEmbeddedBuilderView) {
      return cameraZoom;
    }
    const linkedCameraZoom = linkedAvatarConfig?.cameraZoom;
    return typeof linkedCameraZoom === 'number' ? linkedCameraZoom : cameraZoom;
  }, [cameraZoom, isEmbeddedBuilderView, linkedAvatarConfig]);

  const motionParallaxEnabled = useMemo(() => {
    if (isEmbeddedBuilderView) {
      return false;
    }

    return linkedAvatarConfig?.offerGesture === true;
  }, [isEmbeddedBuilderView, linkedAvatarConfig]);

  const effectiveSkipTeethAnimation = useMemo(() => {
    const linkedSkipTeethAnimation = linkedAvatarConfig?.skipTeethAnimation;
    if (typeof linkedSkipTeethAnimation === 'boolean') {
      return linkedSkipTeethAnimation;
    }

    // Do not force-skip teeth in live mode. Sam's legacy mesh (and several
    // FBX rigs) open the mouth primarily via the lower-teeth morph; skipping
    // it made face/mouth look frozen while visemes still ran on the body.
    // Callers that truly want the performance opt set skipTeethAnimation
    // explicitly on the linked config.
    return undefined;
  }, [linkedAvatarConfig]);

  const liveAvatarConfig = useMemo(() => {
    // Pull the project's appearance overrides (skin/eye/hair colours, background)
    // first, then fall back to the model registry's `defaultAppearance` for any
    // fields the project didn't set. Without the registry fallback, projects
    // that don't customise appearance render with whatever the GLB material
    // happens to ship with — which for several of our models is unusably wrong
    // (e.g. green eyes on a model that should have hazel). Ported from the
    // retired builder VisualChannel onReady handler (commit 1625edfe).
    const savedAppearance =
      savedModelConfig?.appearance && typeof savedModelConfig.appearance === 'object'
        ? { ...(savedModelConfig.appearance as Record<string, unknown>) }
        : {};

    const projectAppearance =
      linkedAvatarConfig?.appearance && typeof linkedAvatarConfig.appearance === 'object'
        ? { ...(linkedAvatarConfig.appearance as Record<string, unknown>) }
        : {};

    const projectFlatColors: Record<string, unknown> = {};
    if (linkedAvatarConfig && typeof linkedAvatarConfig.skinColor === 'string') {
      projectFlatColors.skinColor = linkedAvatarConfig.skinColor;
    }
    if (linkedAvatarConfig && typeof linkedAvatarConfig.eyeColor === 'string') {
      projectFlatColors.eyeColor = linkedAvatarConfig.eyeColor;
    }
    if (linkedAvatarConfig && typeof linkedAvatarConfig.hairColor === 'string') {
      projectFlatColors.hairColor = linkedAvatarConfig.hairColor;
    }
    if (linkedAvatarConfig && typeof linkedAvatarConfig.clothingColor === 'string') {
      projectFlatColors.clothingColor = linkedAvatarConfig.clothingColor;
    }
    if (linkedAvatarConfig && typeof linkedAvatarConfig.shirtColor === 'string') {
      projectFlatColors.clothingColor = linkedAvatarConfig.shirtColor;
    }

    const merged: Record<string, unknown> = {
      ...(savedModelConfig ?? {}),
      ...(linkedAvatarConfig ?? {}),
      appearance: {
      ...savedAppearance,
      ...projectFlatColors,
      ...projectAppearance
      },
    };

    return mergeAvatarRuntimeConfig({
      modelId: activeAvatarModel.id,
      savedConfig: savedModelConfig,
      hostOverrides: merged
    });
  }, [activeAvatarModel.id, linkedAvatarConfig, savedModelConfig]);

  const liveAvatarAppearance = liveAvatarConfig?.appearance ?? null;

  // Quality resolution — see resolveRequestedAvatarQuality above. An
  // explicit HD/SD saved in the avatar settings modal
  // (`linkedAvatarConfig.avatarQuality`, delivered by the settings chain
  // over `visual.linked`) wins on every device; Auto (unset) keeps the
  // per-surface defaults: SD on live surfaces, SD forced on mobile.
  const avatarQuality: AvatarQuality = resolveRequestedAvatarQuality(
    linkedAvatarConfig?.avatarQuality
  );
  if (IS_MOBILE_USER_AGENT && avatarQuality === 'hd' && typeof console !== 'undefined') {
    console.info('[useLiveAvatarConfig] Explicit HD honoured on a mobile user agent — watch for WebGL memory pressure');
  }

  // Single source of truth for the URL the renderer should fetch. Computed
  // here so both render shells (the legacy `<CamlinAvatar>` shell via
  // `sharedLiveAvatarConfig.modelUrl` AND the runtime host's
  // `<Canvas>+<Scene>` path via the `resolvedModelUrl` return value) get the
  // same answer. Fix for mobile-blank-canvas: previously only
  // `sharedLiveAvatarConfig.modelUrl` was SD-aware, but App.tsx hard-codes
  // `useSharedLiveAvatarShell=false`, so that branch never ran and mobile
  // received the HD URL. Returning `resolvedModelUrl` separately lets
  // `AvatarSceneShell` consume the same SD URL on the active code path.
  const resolvedModelUrl = useMemo(
    () => resolveModelUrlForQuality(activeAvatarModel, avatarQuality),
    [activeAvatarModel, avatarQuality]
  );

  const sharedLiveAvatarConfig = useMemo<CamlinAvatarConfig>(() => ({
    mode: 'full',
    showChat: false,
    showButtons: false,
    showLogo: false,
    showAvatarSwitcher: false,
    modelUrl: resolvedModelUrl,
    cameraZoom: effectiveCameraZoom,
    motionParallaxEnabled,
    skipTeethAnimation: effectiveSkipTeethAnimation,
    isMobileCall,
    isWebrtcCall,
    isInVoiceCall,
    isLiveMode: liveModeActive,
    useDefaultMaterials: activeAvatarModel.textureMode === 'hybrid',
    useEmbeddedTextures: activeAvatarModel.textureMode === 'embedded',
    initialAppearance: liveAvatarAppearance as Record<string, string | undefined> | undefined,
    backgroundColor:
      typeof liveAvatarAppearance?.backgroundColor === 'string'
        ? liveAvatarAppearance.backgroundColor
        : undefined
  }), [
    activeAvatarModel,
    resolvedModelUrl,
    effectiveCameraZoom,
    motionParallaxEnabled,
    effectiveSkipTeethAnimation,
    isInVoiceCall,
    isMobileCall,
    isWebrtcCall,
    liveAvatarAppearance,
    liveModeActive
  ]);

  return {
    activeAvatarModel,
    resolvedModelUrl,
    liveAvatarAppearance,
    effectiveCameraZoom,
    motionParallaxEnabled,
    effectiveSkipTeethAnimation,
    liveTtsVoiceId: resolveAvatarTtsVoiceId({
      hostVoiceId: requestedLiveVoiceId,
      settings: savedModelConfig,
      modelId: activeAvatarModel.id,
      modelVoiceId: activeAvatarModel.voiceId
    }),
    sharedLiveAvatarConfig,
    liveAvatarConfig
  };
}

export default useLiveAvatarConfig;
